import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import ViewAsBanner from './ViewAsBanner';
import { getViewAsTabSession } from './viewAsAPI';
import {
  getViewAsSession,
  isViewAsEnded,
  setViewAsSession,
  VIEW_AS_ENDED_EVENT,
  VIEW_AS_PATH,
} from './viewAsSession';

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      px: 3,
      textAlign: 'center',
    }}
  >
    {children}
  </Box>
);

const closeTab = () => window.close();

/** Shown for the rest of the tab's life once its session has ended; the app is unmounted. */
export const ViewAsEnded: React.FC = () => (
  <Centered>
    <Typography variant="h5" component="h1">
      This support session has ended
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Start a new one from the admin console if you still need it.
    </Typography>
    <Button variant="contained" onClick={closeTab}>
      Close tab
    </Button>
  </Centered>
);

/** Reads the session's claims with its token, then loads the app at the dashboard. */
const ViewAsStart: React.FC = () => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getViewAsTabSession()
      .then((session) => {
        if (cancelled) return;
        setViewAsSession(session);
        // A full load, so the app starts with the viewed member's identity from the outset.
        window.location.replace('/Dashboard');
      })
      .catch(() => {
        // A 401 has already ended the session (the "ended" screen replaces this one).
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Centered>
      {failed ? (
        <>
          <Typography variant="h5" component="h1">
            The support session couldn&apos;t start
          </Typography>
          <Button variant="contained" onClick={closeTab}>
            Close tab
          </Button>
        </>
      ) : (
        <>
          <CircularProgress aria-label="Starting support session" />
          <Typography variant="body2">Starting support session…</Typography>
        </>
      )}
    </Centered>
  );
};

/**
 * HA-447: the root of a support view-as tab, in place of the app's own root. It starts the
 * session, then renders the app as the viewed member under the session banner, and swaps both
 * for the "ended" screen when the session ends (End, expiry, a revoked grant), which also stops
 * every request the app would keep making.
 */
const ViewAsRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ended, setEnded] = useState(isViewAsEnded);

  useEffect(() => {
    const onEnded = () => setEnded(true);

    window.addEventListener(VIEW_AS_ENDED_EVENT, onEnded);

    return () => window.removeEventListener(VIEW_AS_ENDED_EVENT, onEnded);
  }, []);

  if (ended) {
    return <ViewAsEnded />;
  }

  if (!getViewAsSession() || window.location.pathname.toLowerCase() === VIEW_AS_PATH) {
    return <ViewAsStart />;
  }

  return (
    <>
      {children}
      <ViewAsBanner />
    </>
  );
};

export default ViewAsRoot;
